import React, {createRef, RefObject} from 'react';
import {mediaQueries, StyledProps} from '../../modules/common';
import {AnimationCard} from './animation-card';
import {styled} from 'styled-components';
import {LottieAnimation} from '../lottie-animation';
import {darken, transparentize} from 'polished';
import Swiper from 'react-id-swiper';
//import { Swiper } from 'swiper/react'
import DefaultSwiper from 'swiper';

/**
 * Component to hold cards and reproduce them in series
 *
 * Must be passed as props:
 *      cardsContent: AnimationCardContent[]; -> Array where each item has the info to render each card in the holder (title, content, animation path and animation palceholder)
 *
 * Can be passed as props:
 *      automaticScrollOnMobile?: boolean;    -> To define the behavior of the slide when the set of cards does not fit into the screen
 *      cardsMaxWidth?: number;               -> Max width for each card on desktop
 *      centerContentText?: boolean;          -> To center each card content
 *      textMargin?: number;                  -> to apply left and right margin to each card content
 *
 *
 * ```````Behavior
 * When the set of cards fits the screen and enters the viewport the animation of each card starts to reproduce one after the other.
 * When they are all finished each animations can start again separately by clicking on them (This does not imply any cycle)
 *
 * NOTES:
 * -Each time the component enters the viewport the cycle starts again.
 * -If the cycle is not complete entering and leaving the viewprot does not start or ends the cycle.
 * -If cardsMaxWidth is not provided the max width set will be the one specified in the theme
 * -textMargin will NOT be applied to the title and animation in each card, only the content
 * -centerContentText and textMargin will NOT be applied on mobile
 *
 *
 * When the set of cards does not fit into the screen if
 * (1)---automaticScrollOnMobile is undefined or automaticScrollOnMobile = false:
 *      each animation can be start by accordingly clicking the arrow buttons that will appear.
 * (2)---automaticScrollOnMobile = true
 *      Each animation will start one after the other and the slider will move to the current animation to play.
 *      During the cycle the arrow buttons will be disabled
 *      After the cycle is complete the animations can be started again as in (1)
 *
 * ````example
 * const card1= new AnimationCardContent(
 *              'Card1',
 *              'Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.',
 *              '/static/landings/animations/clock.json',
 *              <ClockPlaceholder/>);
 *
 * <AnimationCardHolder cardsContent={[card1,card1,card1]} automaticScrollOnMobile={false}/>
 * ````
 */

const Direction = {
    LEFT: -1,
    RIGHT: 1,
};

interface Props extends StyledProps {
    cardsContent: AnimationCardContent[];
    cardsMaxWidth?: number;
    centerContentText?: boolean;
    textMargin?: number;
}

interface State {
    currentToPlay: number;
    completed: boolean;
    overflown: boolean;
    disabledRight: boolean;
    disabledLeft: boolean;
}

class UnstyledAnimationCardHolder extends React.Component<Props, State> {
    private references: RefObject<LottieAnimation>[] = [];
    private swiper: DefaultSwiper = null;

    constructor(props: Props) {
        super(props);

        const {cardsContent} = this.props;

        cardsContent.forEach(() => {
            this.references.push(createRef<LottieAnimation>());
        });

        const current = 0;

        this.state = {
            currentToPlay: current,
            completed: false,
            overflown: false,
            disabledRight: this.isRightButtonDisabled(),
            disabledLeft: this.isLeftButtonDisabled(),
        };
    }

    componentDidMount(): void {
        window.addEventListener('resize', this.onResizeHandler);
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.onResizeHandler);
    }

    play(i: number): void {
        if (i >= 0 && i < this.references.length)
            if (this.references[i].current) this.references[i].current.play();
    }

    canFirstAnimationStart = (): boolean => {
        const {currentToPlay, completed} = this.state;

        return currentToPlay === 0 || completed;
    };

    actionOnComplete = (i: number): void => {
        const {overflown} = this.state;

        if (!overflown) {
            this.play(i);

            const completed = this.references.length <= i;
            this.setState({completed: completed});
        }
    };

    onStartAnimation = (i: number): void => {
        const {overflown} = this.state;

        if (!overflown || this.references[i].current.startedOnScroll())
            this.setState({currentToPlay: i, completed: false});
    };

    onResizeHandler = (): void => {
        this.setOverflownState();
        const swiper = this.swiper;

        if (swiper !== null) swiper.slideTo(0);

        const newDisabledLeft = this.isLeftButtonDisabled();
        const newDisabledRight = this.isRightButtonDisabled();

        this.setState({
            disabledLeft: newDisabledLeft,
            disabledRight: newDisabledRight,
            currentToPlay: 0,
        });
    };

    setOverflownState = (): void => {
        const swiper = this.swiper;

        if (swiper != null) {
            swiper.update();
            const overflown = !(swiper.isBeginning && swiper.isEnd);

            if (this.state.overflown != overflown)
                this.setState({overflown: overflown});
        }
    };

    isLeftButtonDisabled = (): boolean => {
        if (this.swiper !== null) return this.swiper.isBeginning;
    };

    isRightButtonDisabled = (): boolean => {
        if (this.swiper !== null) {
            return this.swiper.isEnd;
        }
    };

    onClickButton = (direction: number): void => {
        const swiper = this.swiper;

        if (swiper != null) {
            if (Direction.RIGHT === direction) swiper.slideNext();
            else swiper.slidePrev();
        }
    };

    fromEdge = (): void => {
        const {overflown} = this.state;
        const swiper = this.swiper;

        if (swiper != null && overflown) this.onPlayCard(swiper.activeIndex);

        this.setButtonsState();
    };

    setButtonsState = (): void => {
        this.setState({
            disabledRight: this.isRightButtonDisabled(),
            disabledLeft: this.isLeftButtonDisabled(),
        });
    };

    onSlideChange = (): void => {
        const {overflown} = this.state;
        const swiper = this.swiper;

        if (swiper != null && overflown) {
            this.onPlayCard(swiper.activeIndex);
        }
    };

    onEndReached = (): void => {
        const swiper = this.swiper;
        if (swiper !== null && swiper.isEnd)
            for (let i = swiper.activeIndex + 1; i < swiper.slides.length; i++)
                this.onPlayCard(i);

        this.setButtonsState();
    };

    onBeginningReached = (): void => {
        this.setButtonsState();
    };

    onPlayCard = (currentToPlay: number): void => {
        this.play(currentToPlay);

        this.setState({
            currentToPlay: currentToPlay,
        });
    };

    onInitSwiper = (swiper: DefaultSwiper): void => {
        this.swiper = swiper;
        this.setOverflownState();
    };

    render(): JSX.Element {
        const {
            cardsContent,
            className,
            cardsMaxWidth,
            centerContentText,
            textMargin,
        } = this.props;
        const {
            currentToPlay,
            completed,
            overflown,
            disabledLeft,
            disabledRight,
        } = this.state;

        const params = {
            slidesPerView: 'auto' as const,
            watchOverflow: true,
            on: {
                fromEdge: this.fromEdge,
                slideChange: this.onSlideChange,
                reachEnd: this.onEndReached,
                reachBeginning: this.onBeginningReached,
                init: this.onInitSwiper,
            },
        };

        const cards = cardsContent.map((card, i) => {
            const autoplay = i === 0;
            const content = card.path ? (
                <div>
                    <Animation
                        ref={this.references[i]}
                        path={card.path}
                        startWhenVisible={autoplay}
                        autoplay={autoplay}
                        startOnClick={completed && !overflown}
                        height={144}
                        actionOnComplete={(): void =>
                            this.actionOnComplete(i + 1)
                        }
                        actionOnPlay={(): void => this.onStartAnimation(i)}
                        canStart={this.canFirstAnimationStart}
                    >
                        {card.placeholder}
                    </Animation>
                </div>
            ) : (
                card.placeholder
            );

            const params = {
                title: card.title,
                content: card.content,
                highlighted: i === currentToPlay && !completed,
                maxWidth: cardsMaxWidth,
                centerContentText: centerContentText,
                textMargin: textMargin,
                teloaCard:
                    (process.env.NEXT_PUBLIC_PROJECT_NAME as string) ===
                    'teloa',
            };

            if (i === 0)
                return (
                    <SlideWrapper key={i}>
                        <FirstAnimation {...params}>{content}</FirstAnimation>
                    </SlideWrapper>
                );

            if (i === cardsContent.length - 1)
                return (
                    <SlideWrapper key={i}>
                        <LastAnimation {...params}>{content}</LastAnimation>
                    </SlideWrapper>
                );

            return (
                <SlideWrapper key={i}>
                    <AnimationCard {...params}>{content}</AnimationCard>
                </SlideWrapper>
            );
        });

        return (
            <div className={className}>
                <Container>
                    <Swiper {...params}>{cards}</Swiper>
                </Container>

                <FlexDiv $visible={overflown}>
                    <Button
                        data-testid="left-button"
                        onClick={() => {
                            this.onClickButton(Direction.LEFT);
                        }}
                        disabled={disabledLeft}
                        aria-label={'left'}
                    >
                        {/* eslint-disable-next-line @next/next/no-img-element*/}
                        <img
                            src="/static/landings/miscellanea/left.svg"
                            alt={'left'}
                        />
                    </Button>

                    <Button
                        data-testid="right-button"
                        onClick={() => {
                            this.onClickButton(Direction.RIGHT);
                        }}
                        disabled={disabledRight}
                        aria-label={'right'}
                    >
                        {/* eslint-disable-next-line @next/next/no-img-element*/}
                        <img
                            src="/static/landings/miscellanea/right.svg"
                            alt={'right'}
                        />
                    </Button>
                </FlexDiv>
            </div>
        );
    }
}

const Button = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;

    border: none;
    background-color: ${(props): string =>
        transparentize('0.85', props.theme.colors.tiburon)};
    border-radius: 50%;
    width: 40px;
    height: 40px;
    color: ${(props): string => props.theme.colors.tiburon};
    margin: 24px 16px 8px 16px;
    -webkit-tap-highlight-color: transparent;

    ${(props): string =>
        !props.disabled &&
        `@media(hover:hover){
        &:hover {
            color: ${darken('0.1', props.theme.colors.tiburon)};
            background-color: ${transparentize(
                '0.75',
                props.theme.colors.tiburon
            )};
            cursor: pointer;
        }
    }
        
    &:focus {
        outline-style: none;
    }
    `}

    ${(props): string =>
        props.disabled &&
        `color: ${props.theme.colors.tiburon};
         opacity: 0.2;`};
`;

interface HideableProps {
    $visible?: boolean;
}

const FlexDiv = styled.div<HideableProps>`
    display: flex;

    width: fit-content;

    margin-left: auto;
    margin-right: auto;

    ${(props): string => props.$visible === false && `display: none;`}
`;

const SlideWrapper = styled.div`
    width: fit-content;
    display: flex;
`;

const Container = styled.div`
    display: flex;
`;

const Animation = styled(LottieAnimation)`
    width: 224px;
    svg {
        height: 144px;
    }
`;

const FirstAnimation = styled(AnimationCard)`
    ${mediaQueries.lessThan('small')`
            margin-left:16px;
        `}
`;

const LastAnimation = styled(AnimationCard)`
    ${mediaQueries.lessThan('small')`
            margin-right:16px;
        `}
`;

class AnimationCardContent {
    title: string;
    content: string[];
    path: string;
    placeholder: React.ReactNode;

    constructor(
        title: string,
        content: string[],
        path: string,
        placeholder: React.ReactNode
    ) {
        this.title = title;
        this.content = content;
        this.path = path;
        this.placeholder = placeholder;
    }
}

const AnimationCardHolder = styled(UnstyledAnimationCardHolder)`
    padding: 40px 0 64px;

    ${mediaQueries.lessThan('medium')`
        padding: 24px 8px;
    `}
`;

export {
    AnimationCardHolder,
    UnstyledAnimationCardHolder,
    AnimationCardContent,
    Button,
    Direction,
    FlexDiv,
};
